import type { Locale } from "../shared/dto";

const CZECH_HOUR_WORDS = [
  "nula",
  "jedna",
  "dvě",
  "tři",
  "čtyři",
  "pět",
  "šest",
  "sedm",
  "osm",
  "devět",
  "deset",
  "jedenáct",
  "dvanáct",
  "třináct",
  "čtrnáct",
  "patnáct",
  "šestnáct",
  "sedmnáct",
  "osmnáct",
  "devatenáct",
  "dvacet",
  "jednadvacet",
  "dvacet dva",
  "dvacet tři"
] as const;

export function czechTimePreposition(hour: number): "v" | "ve" {
  const spokenHour = CZECH_HOUR_WORDS[hour];
  return spokenHour && /^(?:dv|tř|čt)/u.test(spokenHour) ? "ve" : "v";
}

/**
 * F116: the ONE mapping from our two locales to a BCP 47 tag, so no surface
 * hand-picks its own. English is `en-US`: we sell to America, and `en-GB`
 * prints "4 Aug 2026 14:32" - day before month and a 24-hour clock, both of
 * which an American reads as a mistake.
 */
export const INTL_LANGUAGE = { cs: "cs-CZ", en: "en-US" } as const satisfies Record<Locale, string>;

export function intlLanguage(locale: Locale): string {
  return INTL_LANGUAGE[locale];
}

/**
 * The clock fields of a time-only label. `hour: "2-digit"` is right for the
 * Czech 24-hour clock ("09:05") and wrong for the American one, where the same
 * request prints "09:05 AM" for a time every American writes as "9:05 AM".
 * The leading zero is not a rendering detail there, it reads as machine output.
 */
export function clockFields(locale: Locale): { hour: "numeric" | "2-digit"; minute: "2-digit" } {
  return { hour: locale === "cs" ? "2-digit" : "numeric", minute: "2-digit" };
}

/**
 * The note/recording timestamp shared by every card, client and partner alike
 * ("11 Aug 2026, 2:32 PM" / "11. 8. 2026 14:32"). Czech copy used to be baked
 * in here, so an English document printed an English sentence around a Czech
 * month name.
 *
 * The zone stays Europe/Prague on purpose: that is the team's clock and every
 * other surface in the product still states it. Moving to the client's zone is
 * a separate decision (it changes the DATE around midnight, so it must land
 * everywhere at once), tracked in the audit as its own item.
 */
export function formatMediumDateTime(value: string, locale: Locale): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat(intlLanguage(locale), {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/Prague"
  }).format(date);
}

/** Cover meta of a document: "11 August 2026" / "11. srpna 2026". */
export function formatLongDate(value: string | Date, locale: Locale): string | undefined {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return undefined;
  return new Intl.DateTimeFormat(intlLanguage(locale), {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/Prague"
  }).format(date);
}

export function formatDateTime(value: string, locale: Locale): string {
  const date = new Date(value);
  const formatted = new Intl.DateTimeFormat(intlLanguage(locale), {
    dateStyle: "full",
    timeStyle: "short",
    timeZone: "Europe/Prague"
  }).format(date);
  if (locale !== "cs") return formatted;

  const hour = Number(new Intl.DateTimeFormat("cs-CZ", {
    hour: "numeric",
    hourCycle: "h23",
    timeZone: "Europe/Prague"
  }).format(date));
  return formatted.replace(/\bv (?=\d{1,2}:\d{2}\b)/u, `${czechTimePreposition(hour)} `);
}
